/*
 * Order suggestions v2 (owner decision 10-19; BlinkScribe order_suggest_v2, enabled providers only: Dr. Madhu).
 *
 * BlinkScribe re-reads the accumulated transcript every few minutes with the Dr. Madhu study's predictor and
 * once more when the recording stops. Its payload (websocket "order_suggestions_v2", then
 * GET /v1/recording/order-suggestions/visit after the stop) is
 *   { status: "ok" | "pending", final, appointmentid, patientid,
 *     suggestions: [{ concept, order_name, variants, order_type, evidence, evidence_label, confidence,
 *                     prechecked, reason, chart_reason }],
 *     discussed: [{ concept, order_name, order_type, evidence, evidence_label }],   // deferred / declined / past
 *     already_placed: [{ concept, order_name, order_type }],
     other: [{ order_name, order_type }] }     // LiveScribe suggestions outside the study (referrals, imaging, Rx)
 * Only a status "ok" payload of THIS visit replaces the LiveScribe list; anything else (no v2 for this
 * provider, a pending or failed pass) leaves today's suggestions exactly as they are.
 * Pre-checked = the provider's checkbox starts ticked (flu vaccine after the final pass); nothing is ordered
 * until the provider adds it (the row's ⊕ or "Add checked").
 *
 * Pure: no React, no network.
 */
import { orderKey } from "./orderKey";

export const V2_STORE_PREFIX = "careiq:orderV2:";

export const v2StoreKey = (patientId, apptKey) => (patientId && apptKey ? `${V2_STORE_PREFIX}${patientId}:${apptKey}` : null);

// A payload this visit may show: status ok, same appointment and patient.
export const isV2ForVisit = (p, patientId, apptKey) => Boolean(
  p && typeof p === "object" && p.status === "ok"
  && apptKey && String(p.appointmentid || "") === String(apptKey)
  && patientId && String(p.patientid || "") === String(patientId)
  && Array.isArray(p.suggestions),
);

// The socket announced v2 for this visit (any status): the tab may fetch the final payload after the stop.
export const isV2Announce = (p, patientId, apptKey) => Boolean(
  p && typeof p === "object" && apptKey && String(p.appointmentid || "") === String(apptKey)
  && patientId && String(p.patientid || "") === String(patientId),
);

// Newest wins; a final payload always beats a live one.
export const newerV2 = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  if (Boolean(a.final) !== Boolean(b.final)) return a.final ? a : b;
  return String(b.updated_at || "") >= String(a.updated_at || "") ? b : a;
};

// The tone of an evidence chip (CSS modifier): positive talk, chart-only, discussed-not-today.
export const evidenceTone = (ev) => {
  if (ev === "explicit_order" || ev === "offered_agreed" || ev === "done_today") return "talk";
  if (ev === "deferred" || ev === "declined" || ev === "past_result") return "discussed";
  return "chart";
};

/*
 * The suggestion rows the order zone renders.
 *   picks:  Map concept -> the order name the provider picked (variants), else the payload's order_name
 *   checks: Map concept -> boolean the provider set, else the payload's prechecked
 *   isAdded(key) / sessionAdds / busyKeys: the tab's order ledger (a row already on the visit reads "✓ Added")
 * Rows whose concept the payload lists as already placed are never returned (BlinkScribe never sends them).
 */
export const v2Rows = (payload, { aliases = {}, picks = new Map(), checks = new Map(), isAdded = () => false, sessionAdds = new Map(), busyKeys = new Set() } = {}) => {
  if (!payload || !Array.isArray(payload.suggestions)) return [];
  const placed = new Set((payload.already_placed || []).map((x) => x && x.concept));
  const out = [];
  const seen = new Set();
  payload.suggestions.forEach((s) => {
    if (!s || !s.concept || placed.has(s.concept)) return;
    const variants = Array.isArray(s.variants) && s.variants.length ? s.variants.filter((v) => typeof v === "string" && v.trim()) : [s.order_name];
    const picked = picks.get(s.concept);
    const name = String((picked && variants.includes(picked) ? picked : s.order_name) || variants[0] || "").trim();
    const key = orderKey(name, aliases);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const added = isAdded(key);
    const sa = sessionAdds.get(key);
    out.push({
      key,
      concept: s.concept,
      name,
      variants,
      order_type: s.order_type || null,
      details: s.reason || "",
      evidence: s.evidence || "chart",
      evidenceLabel: s.evidence_label || "",
      tone: evidenceTone(s.evidence),
      reason: s.reason || "",
      chartReason: s.chart_reason || "",
      confidence: typeof s.confidence === "number" ? s.confidence : null,
      prechecked: Boolean(s.prechecked),
      checked: !added && (checks.has(s.concept) ? Boolean(checks.get(s.concept)) : Boolean(s.prechecked)),
      added,
      busy: busyKeys.has(key),
      pre: added && !(sa && sa.from === "live"),
    });
  });
  return out;
};

// Today's LiveScribe suggestions the study does not cover (referrals, outside imaging, prescriptions, free
// text): listed as before with the plain add; a name that keys like a v2 row is not repeated.
export const v2Other = (payload, { aliases = {}, isAdded = () => false, sessionAdds = new Map(), busyKeys = new Set(), skipKeys = [] } = {}) => {
  if (!payload || !Array.isArray(payload.other)) return [];
  const seen = new Set(skipKeys);
  const out = [];
  payload.other.forEach((o) => {
    const name = String((o && o.order_name) || "").replace(/^Order\s+/i, "").trim();
    const key = orderKey(name, aliases);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const added = isAdded(key);
    const sa = sessionAdds.get(key);
    out.push({ key, name, order_type: (o && o.order_type) || null, details: "", added, busy: busyKeys.has(key), pre: added && !(sa && sa.from === "live") });
  });
  return out;
};

export const v2Discussed = (payload) => (payload && Array.isArray(payload.discussed) ? payload.discussed.filter((d) => d && d.concept && d.order_name) : []);

export const v2Placed = (payload) => (payload && Array.isArray(payload.already_placed) ? payload.already_placed.filter((d) => d && d.concept && d.order_name) : []);
